import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth.js";
import { Security } from "./lib/Security.js";
import { PageMemory, rememberedPath } from "./lib/PageMemory.js";
import { Login } from "./routes/Login.js";
import { BookingPage } from "./book/BookingPage.js";
import { SetPassword } from "./routes/SetPassword.js";
import { RequireAuth, RequireBusinessMember, RequireManager, RequireOwner, RequirePlatformAdmin } from "./routes/Guards.js";
import { AppShell } from "./app/AppShell.js";
import { Dashboard } from "./app/Dashboard.js";
import { Calls } from "./app/Calls.js";
import { Callbacks } from "./app/Callbacks.js";
import { Jobs } from "./app/Jobs.js";
import { PhotoPage } from "./photos/PhotoPage.js";
import { Bookings } from "./app/Bookings.js";
import { Customers } from "./app/Customers.js";
import { MyHours } from "./app/MyHours.js";
import { Services } from "./app/Services.js";
import { Rules } from "./app/Rules.js";
import { Settings } from "./app/Settings.js";
import { Invoices } from "./app/Invoices.js";
import { AdminShell } from "./admin/AdminShell.js";
import { ClientsList } from "./admin/ClientsList.js";
import { AgentConfig } from "./admin/AgentConfig.js";
import { IndustryRules } from "./admin/IndustryRules.js";
import { VoiceInstructions } from "./admin/VoiceInstructions.js";
import { Licenses } from "./admin/Licenses.js";
import { RateCards } from "./admin/RateCards.js";
import { Communication } from "./admin/Communication.js";
import { WebBooking } from "./admin/WebBooking.js";
import { VoipUsage } from "./admin/VoipUsage.js";
import { CallDebug } from "./admin/CallDebug.js";
import { Invoices as AdminInvoices } from "./admin/Invoices.js";
import { AuditLog } from "./admin/AuditLog.js";
import { DeveloperInfo } from "./admin/DeveloperInfo.js";

/** A pure platform-admin account (no business_members rows of its own) has nowhere to go in
 *  /app -- RequireBusinessMember would just show "not linked to a business". Route those
 *  straight to /admin instead of hardcoding /app for everyone. */
function RootRedirect() {
  const { memberships, isPlatformAdmin } = useAuth();
  if (memberships.length === 0 && isPlatformAdmin) return <Navigate to="/admin" replace />;
  // Reopening the app (e.g. from the home screen) lands on the page you were last on; a guard still
  // sends you elsewhere if you no longer have access to it.
  return <Navigate to={rememberedPath() ?? "/app"} replace />;
}

export function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <PageMemory />
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* Public: customers book here without signing in (docs: online booking) */}
          <Route path="/book/:slug" element={<BookingPage />} />
          <Route path="/photos/:token" element={<PhotoPage />} />

          <Route element={<RequireAuth />}>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/set-password" element={<SetPassword />} />

            <Route element={<RequireBusinessMember />}>
              <Route path="/app" element={<AppShell />}>
                {/* Everyone, including a staff login (one staff member's own view) */}
                <Route path="new-booking" element={<Bookings view="schedule" />} />
                <Route path="bookings" element={<Bookings view="list" />} />
                <Route path="my-hours" element={<MyHours />} />
                <Route path="security" element={<Security />} />
                <Route element={<RequireManager />}>
                  <Route index element={<Dashboard />} />
                  <Route path="calls" element={<Calls />} />
                  <Route path="callbacks" element={<Callbacks />} />
                  <Route path="jobs" element={<Jobs />} />
                  <Route path="customers" element={<Customers />} />
                  <Route path="services" element={<Services />} />
                  <Route element={<RequireOwner />}>
                    <Route path="rules" element={<Rules />} />
                    <Route path="settings" element={<Settings />} />
                    <Route path="invoices" element={<Invoices />} />
                  </Route>
                  {/* Old pages, now sections of Business settings */}
                  <Route path="knowledge" element={<Navigate to="/app/settings" replace />} />
                  <Route path="routes" element={<Navigate to="/app/settings" replace />} />
                  <Route path="team" element={<Navigate to="/app/settings" replace />} />
                </Route>
              </Route>
            </Route>

            <Route element={<RequirePlatformAdmin />}>
              <Route path="/admin" element={<AdminShell />}>
                <Route index element={<ClientsList />} />
                <Route path="licenses" element={<Licenses />} />
                <Route path="rate-cards" element={<RateCards />} />
                <Route path="agent-config" element={<AgentConfig />} />
                <Route path="industry-rules" element={<IndustryRules />} />
                <Route path="voice-instructions" element={<VoiceInstructions />} />
                <Route path="communication" element={<Communication />} />
                <Route path="numbers" element={<Navigate to="/admin/communication?tab=numbers" replace />} />
                <Route path="sms" element={<Navigate to="/admin/communication?tab=texts" replace />} />
                <Route path="web-booking" element={<WebBooking />} />
                <Route path="voip-usage" element={<VoipUsage />} />
                <Route path="call-debug" element={<CallDebug />} />
                <Route path="invoices" element={<AdminInvoices />} />
                <Route path="developer-info" element={<DeveloperInfo />} />
                {/* Old addresses, kept so bookmarks and remembered pages still land */}
                <Route path="billing-settings" element={<Navigate to="/admin/developer-info" replace />} />
                <Route path="support-contact" element={<Navigate to="/admin/developer-info" replace />} />
                <Route path="audit-log" element={<AuditLog />} />
                <Route path="security" element={<Security />} />
              </Route>
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
